<template>
  <div ref="galleryRef" class="gitex-gallery">
    <div
      v-for="(video, index) in videos"
      :key="video + index"
      class="gitex-gallery__slide"
      @touchstart="touchstart"
      @touchend="touchend"
      @click="click"
    >
      <img
        :src="video"
        class="gitex-gallery__slide-video"
        :class="{active: active === index}"
      />
    </div>
    <div class="gitex-gallery__nav">
      <div
        v-for="(_, index) in videos"
        :key="index + _"
        class="gitex-gallery__nav-dot"
        :class="[
          {active: active === index},
          {prev: active - 1 === index},
          {next: active + 1 === index},
        ]"
        @click="changeSlide(index)"
      >
        <div
          class="gitex-gallery__nav-progress"
          :style="{
            transform:
              active === index ? `translateX(${progress}%)` : 'none',
          }"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import {useRuntimeConfig} from '#imports';
const runtimeConfig = useRuntimeConfig();
const staticUrl = runtimeConfig.public.vueAppStaticUrl;

const videos = [
  `${staticUrl}/images/p-gitex-00.webp`,
  `${staticUrl}/images/p-gitex-01.webp`,
  `${staticUrl}/images/p-gitex-02.webp`,
  `${staticUrl}/images/p-gitex-03.webp`,
  `${staticUrl}/images/p-gitex-04.webp`,
  `${staticUrl}/images/p-gitex-05.webp`,
  `${staticUrl}/images/p-gitex-06.webp`,
  `${staticUrl}/images/p-gitex-07.webp`,
  `${staticUrl}/images/p-gitex-08.webp`,
  `${staticUrl}/images/p-gitex-09.webp`,
  `${staticUrl}/images/p-gitex-10.webp`,
  `${staticUrl}/images/p-gitex-11.webp`,
];

const active = ref(0);
const progress = ref(0);
const galleryRef = ref();
const autoplayTime = 4000;

const interval = ref<ReturnType<typeof setInterval>>();
const timer = ref<ReturnType<typeof setTimeout>>();
const currentTime = ref(0);

function changeSlide(slide: number) {
  let currentSlide;

  if (slide > videos.length - 1) {
    currentSlide = 0;
  } else if (slide < 0) {
    currentSlide = videos.length - 1;
  } else {
    currentSlide = slide;
  }

  if (interval.value && timer) {
    clearInterval(interval.value);
    clearTimeout(timer.value);
  }

  active.value = currentSlide;
  currentTime.value = 0;
  progress.value = 0;

  startPlay();
}

function touchstart(e: any) {
  touchstartX = e.changedTouches[0].screenX;
  touchstartY = e.changedTouches[0].screenY;
}

function touchend(e: any) {
  touchendX = e.changedTouches[0].screenX;
  touchendY = e.changedTouches[0].screenY;
  handleGesture(touchstartX, touchstartY, touchendX, touchendY);
}

function click(e: any) {
  const {x} = e.target.getBoundingClientRect();

  if (e.clientX - x >= e.target.clientWidth / 2) {
    changeSlide(active.value + 1);
  } else {
    changeSlide(active.value - 1);
  }
}

let touchstartX = 0;
let touchstartY = 0;
let touchendX = 0;
let touchendY = 0;

function handleGesture(
  touchstartX: number,
  touchstartY: number,
  touchendX: number,
  touchendY: number,
) {
  const delx = touchendX - touchstartX;
  const dely = touchendY - touchstartY;
  if (Math.abs(delx) > Math.abs(dely)) {
    if (delx > 0) {
      changeSlide(active.value - 1);
    } else {
      changeSlide(active.value + 1);
    }
  }
}

function startPlay() {
  interval.value = setInterval(() => {
    currentTime.value += 200;
    progress.value = Math.floor(
      (currentTime.value / autoplayTime) * 100,
    );
  }, 200);
  timer.value = setTimeout(
    () => changeSlide(active.value + 1),
    autoplayTime,
  );
}

function scrollHandler() {
  if (
    window.scrollY +
      galleryRef.value.getBoundingClientRect().top +
      galleryRef.value.clientHeight / 2 <
    window.scrollY + document.documentElement.clientHeight
  ) {
    changeSlide(0);
    document.removeEventListener('scroll', scrollHandler);
  }
}

onMounted(() => {
  document.addEventListener('scroll', scrollHandler);
});

onUnmounted(() => {
  clearInterval(interval.value);
  clearTimeout(timer.value);
  document.removeEventListener('scroll', scrollHandler);
});
</script>

<style scoped lang="scss">
.gitex-gallery {
  width: 100%;
  height: 634px;
  position: relative;

  &__slide {
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    display: inline-block;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      border-radius: 32px;
      transition: all 0.3s ease-in-out;
      opacity: 0;
      user-select: none;

      &.active {
        opacity: 1;
      }
    }
  }

  &__nav {
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    bottom: 32px;
    display: flex;
    justify-content: center;
    gap: 8px;

    &-progress {
      position: absolute;
      top: 0;
      width: 100%;
      height: 100%;
      background: #ffffff;
      right: 100%;
      opacity: 0;
    }

    &-dot {
      width: 40px;
      height: 4px;
      background: rgba(255, 255, 255, 0.6);
      border-radius: 4px;
      position: relative;
      overflow: hidden;
      cursor: pointer;

      &.active {
        .gitex-gallery__nav-progress {
          opacity: 1;
          transition: opacity 0.2s ease-in-out, transform 0.3s linear;
        }
      }
    }
  }

  @media (max-width: $lg) {
    min-height: 223px;
    height: initial;
    &__nav {
      overflow: hidden;
      width: 96px;
      height: 6px;
      bottom: 18px;

      &-dot {
        width: 24px;
        height: 4px;
        position: absolute;
        transition: 0.2s transform, 0.2s left;
        transform: scale(0);
        opacity: 0.8;

        &.prev,
        &.next {
          transform: scale(0.66);
        }

        &.prev {
          left: 0;
        }

        &.next {
          left: 56px;
        }

        &.active {
          transition: 0.2s transform, 0.2s left;
          transform: scale(1);
          opacity: 1;
          left: 28px;
        }
      }
    }
  }
}
</style>
